import React, { useEffect, useState, useMemo } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import Modal from '../components/Modal';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';

const MyAssignments = () => {
  const { toast } = useToast();
  const [tasks, setTasks]             = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [tab, setTab]                 = useState('pending');
  const [submitModal, setSubmitModal] = useState(null);
  const [file, setFile]               = useState(null);
  const [notes, setNotes]             = useState('');

  const load = async () => {
    try {
      setLoading(true); setError('');
      const [t, s] = await Promise.all([
        apiService.getAll('assignments'),
        apiService.get('assignments/my-submissions'),
      ]);
      setTasks(t || []);
      setMySubmissions(Array.isArray(s) ? s : []);
    } catch (err) { setError(err.response?.data?.message || 'Failed to load.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const subMap = useMemo(() =>
    mySubmissions.reduce((acc, s) => { acc[s.taskId] = s; return acc; }, {}),
    [mySubmissions]);

  const pending  = tasks.filter(t => !subMap[t.id] && t.status === 'open');
  const submitted = tasks.filter(t => subMap[t.id]);
  const closed   = tasks.filter(t => !subMap[t.id] && t.status === 'closed');

  const handleSubmit = async e => {
    e.preventDefault();
    if (!file) { setError('Please select a file.'); return; }
    try {
      setSubmitting(true); setError(''); setSuccess('');
      const fd = new FormData();
      fd.append('taskId', submitModal.id);
      fd.append('file',   file);
      fd.append('notes',  notes);
      await apiService.raw.post('assignments/submit', fd);
      setSuccess('Submission uploaded successfully!');
      toast('Assignment submitted successfully!', 'success');
      setSubmitModal(null); setFile(null); setNotes('');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed.');
      toast(err.response?.data?.message || 'Submission failed.', 'error');
    } finally { setSubmitting(false); }
  };

  const isOverdue = dueDate => new Date(dueDate) < new Date();

  const statusBadge = status => {
    const colors = { submitted: '#1976D2', late: '#F57C00', graded: '#388E3C', returned: '#7B1FA2' };
    return <span style={{ background: `${colors[status] || '#666'}22`, color: colors[status] || '#666', padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600 }}>{status}</span>;
  };

  if (loading) return <LoadingSpinner message="Loading assignments..." />;

  const currentList = tab === 'pending' ? pending : tab === 'submitted' ? submitted : closed;

  return (
    <div className="page-container">
      <div className="page-header"><h1>My Assignments</h1></div>

      {error   && <div className="error-message">{error}</div>}
      {success && <div style={{ background: '#d4edda', color: '#155724', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem' }}>{success}</div>}

      <div className="messages-tabs" style={{ marginBottom: '1.5rem' }}>
        <button className={`tab-button ${tab === 'pending'   ? 'active' : ''}`} onClick={() => setTab('pending')}>
          Pending ({pending.length})
        </button>
        <button className={`tab-button ${tab === 'submitted' ? 'active' : ''}`} onClick={() => setTab('submitted')}>
          Submitted ({submitted.length})
        </button>
        <button className={`tab-button ${tab === 'closed'    ? 'active' : ''}`} onClick={() => setTab('closed')}>
          Closed ({closed.length})
        </button>
      </div>

      {currentList.length === 0 ? (
        <div className="no-data-message">No {tab} assignments.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {currentList.map(task => {
            const sub = subMap[task.id];
            const overdue = isOverdue(task.dueDate);
            return (
              <div key={task.id} style={{ background: 'var(--card-bg)', border: `1px solid ${overdue && !sub ? '#c62828' : 'var(--border-color)'}`, borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                      <strong style={{ fontSize: '1.05rem' }}>{task.title}</strong>
                      <span style={{ fontSize: '0.78rem', background: '#e3f2fd', color: '#1565c0', padding: '0.1rem 0.4rem', borderRadius: 4 }}>{task.type}</span>
                      {sub && statusBadge(sub.status)}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '0.5rem' }}>
                      {task.className}{(task.moduleName || task.subjectName) && ` · ${task.moduleName || task.subjectName}`} · Max: {task.maxMarks} marks
                    </div>
                    <div style={{ fontSize: '0.85rem', color: overdue && !sub ? '#c62828' : 'var(--text-light)', fontWeight: overdue && !sub ? 600 : 400 }}>
                      Due: {new Date(task.dueDate).toLocaleString()} {overdue && !sub ? '⚠ Overdue' : ''}
                    </div>
                    {task.instructions && <p style={{ fontSize: '0.85rem', margin: '0.5rem 0 0', color: 'var(--text-light)' }}>{task.instructions}</p>}

                    {/* Show grade and feedback if graded */}
                    {sub?.status === 'graded' && (
                      <div style={{ marginTop: '0.75rem', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '0.75rem' }}>
                        <div style={{ fontWeight: 600, color: '#2e7d32' }}>
                          Grade: {sub.marksObtained} / {task.maxMarks} ({task.maxMarks > 0 ? Math.round((sub.marksObtained / task.maxMarks) * 100) : 0}%)
                        </div>
                        {sub.feedback && <div style={{ marginTop: '0.25rem', fontSize: '0.85rem' }}>Feedback: {sub.feedback}</div>}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                    {tab === 'pending' && (
                      <button className="btn btn-primary" style={{ fontSize: '0.85rem' }} onClick={() => { setSubmitModal(task); setFile(null); setNotes(''); }}>
                        Submit
                      </button>
                    )}
                    {sub && task.allowResubmit && task.status === 'open' && !isOverdue(task.dueDate) && (
                      <button className="btn btn-secondary" style={{ fontSize: '0.85rem' }} onClick={() => { setSubmitModal(task); setFile(null); setNotes(''); }}>
                        Resubmit
                      </button>
                    )}
                    {sub?.downloadUrl && (
                      <button className="btn-icon" style={{ fontSize: '0.82rem' }} onClick={async () => {
                        try {
                          const subId = sub.id;
                          const res = await apiService.raw.get(`assignments/submissions/${subId}/download`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a'); a.href = url;
                          a.setAttribute('download', sub.originalName || 'submission');
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch { alert('Download failed.'); }
                      }}>
                        ⬇ My Submission
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Submit modal */}
      <Modal isOpen={!!submitModal} onClose={() => setSubmitModal(null)} title={`Submit — ${submitModal?.title}`}>
        {submitModal && (
          <form onSubmit={handleSubmit} className="form">
            <div style={{ background: 'var(--bg-secondary)', padding: '0.75rem', borderRadius: 6, marginBottom: '1rem', fontSize: '0.85rem' }}>
              <strong>Due:</strong> {new Date(submitModal.dueDate).toLocaleString()}<br />
              <strong>Allowed types:</strong> {submitModal.allowedTypes}<br />
              <strong>Max file size:</strong> {submitModal.maxFileSizeMB}MB
            </div>
            {submitModal.instructions && (
              <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: 'var(--text-light)' }}>
                <strong>Instructions:</strong> {submitModal.instructions}
              </div>
            )}
            <div className="form-group">
              <label>Upload File *</label>
              <input type="file" onChange={e => setFile(e.target.files[0])} required
                accept={`.${submitModal.allowedTypes.split(',').join(',.')}`} />
            </div>
            <div className="form-group">
              <label>Notes (optional)</label>
              <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'vertical' }} />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setSubmitModal(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Uploading...' : 'Submit Assignment'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default MyAssignments;
